import { toast } from "@/hooks/use-toast";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiErrorMessage } from "@/helpers/get-api-error-message";
import {
  deleteInternalUser,
  getInternalUserByEmail,
  getInternalUsers,
  updateExotelPhone,
  updateOtherExotelPhone,
  updateInternalUserRole,
} from "../services/internal-user";

// For fetching all internal users
export function useInternalUsers({
  page = 0,
  search,
}: {
  page: number;
  search?: string;
}) {
  const { data, isLoading, error, isFetching, isSuccess } = useQuery({
    queryKey: [
      "internal-users",
      {
        page,
        search,
      },
    ],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 3,
    queryFn: () => getInternalUsers({ page, search }),
  });

  return { data, isLoading, error, isFetching, isSuccess };
}

// For fetching a internal user by email
export function useInternalUserByEmail() {
  const { user } = useUser();
  const email = user?.primaryEmailAddress?.emailAddress;
  const { data, isLoading, error, isFetching, isSuccess } = useQuery({
    queryKey: ["internal-user", email ?? ""],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 3,
    enabled: !!email,
    queryFn: () => getInternalUserByEmail({ email: email! }),
  });

  return { data, isLoading, error, isFetching, isSuccess };
}

// For updating the internal user role
export function useUpdateInternalUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInternalUserRole,
    onSuccess: (_data, variables) => {
      setTimeout(() => {
        toast({
          title: "Internal user role updated successfully",
        });

        queryClient.invalidateQueries({ queryKey: ["internal-users"] });
      }, 1000);
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Failed to update Internal user role",
        description: getApiErrorMessage(err, "Failed to update internal user role"),
        variant: "destructive",
      });
    },
  });
}

export function useUpdateExotelPhone() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const email = user?.primaryEmailAddress?.emailAddress;

  return useMutation({
    mutationFn: (phone: string | null) => updateExotelPhone(phone),
    onSuccess: () => {
      toast({ title: "ExoTel number saved" });
      queryClient.invalidateQueries({ queryKey: ["internal-users"] });
      if (email) {
        queryClient.invalidateQueries({ queryKey: ["internal-user", email] });
      }
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Failed to save ExoTel number",
        description: getApiErrorMessage(err, "Could not save the ExoTel number."),
        variant: "destructive",
      });
    },
  });
}

export function useUpdateOtherExotelPhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      target_clerk_id,
      phone,
    }: {
      target_clerk_id: string;
      phone: string | null;
    }) => updateOtherExotelPhone({ target_clerk_id, phone }),
    onSuccess: () => {
      toast({ title: "ExoTel number saved" });
      queryClient.invalidateQueries({ queryKey: ["internal-users"] });
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Failed to save ExoTel number",
        description: getApiErrorMessage(err, "Could not save the ExoTel number."),
        variant: "destructive",
      });
    },
  });
}

// For banning a internal user
export function useRemoveInternalUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInternalUser,
    onSuccess: () => {
      setTimeout(() => {
        toast({
          title: "Internal user removed successfully",
        });
        queryClient.invalidateQueries({ queryKey: ["internal-users"] });
      }, 1000);
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Failed to remove internal user",
        description: getApiErrorMessage(err, "Failed to remove internal user"),
        variant: "destructive",
      });
    },
  });
}
